import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Users, Plus, Search, MessageSquare, Zap, Globe, Star } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { CreateRoomModal } from '../components/Rooms/CreateRoomModal'
import { useNostrRooms } from '../hooks/useNostrRooms'

type TabType = 'all' | 'joined'

export function RoomsPage() {
    const { pubkey } = useAuth()
    const navigate = useNavigate()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [activeTab, setActiveTab] = useState<TabType>('all')
    const [joinedRooms, setJoinedRooms] = useState<string[]>([])
    const { rooms, isLoading } = useNostrRooms()

    // Load joined rooms from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('joinedRooms')
        if (stored) {
            try {
                setJoinedRooms(JSON.parse(stored))
            } catch (e) {
                console.error('Failed to parse joined rooms:', e)
            }
        }
    }, [])

    const handleRoomClick = (roomId: string) => {
        // Mark as joined when entering
        if (!joinedRooms.includes(roomId)) {
            const updated = [...joinedRooms, roomId]
            setJoinedRooms(updated)
            localStorage.setItem('joinedRooms', JSON.stringify(updated))
        }
        navigate(`/rooms/${roomId}`)
    }

    const filteredRooms = rooms
        .filter(room => activeTab === 'all' || joinedRooms.includes(room.identifier))
        .filter(room =>
            room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            room.about.toLowerCase().includes(searchQuery.toLowerCase())
        )

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl p-8 border border-surface-200 dark:border-surface-700 shadow-sm overflow-hidden relative">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                    <Users className="w-48 h-48" />
                </div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <h1 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 dark:text-white">Odalar</h1>
                        <p className="text-gray-500 dark:text-gray-400 font-poppins max-w-xl text-lg">
                            Mavililer topluluğu içinde ilgi alanlarına göre gruplar oluştur, sohbet et ve bilgi paylaş.
                        </p>
                    </div>

                    {pubkey && (
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="flex items-center justify-center gap-2 px-6 py-4 bg-mavi-500 hover:bg-mavi-600 text-white rounded-2xl transition-all shadow-lg shadow-mavi-500/20 font-poppins font-bold group"
                        >
                            <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                            Oda Oluştur
                        </button>
                    )}
                </div>

                {/* Search & Tabs */}
                <div className="mt-8 space-y-4">
                    {/* Tabs */}
                    <div className="flex items-center gap-2 p-1 bg-surface-100 dark:bg-surface-800/50 rounded-xl w-fit">
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`px-4 py-2 text-sm font-poppins font-bold rounded-lg transition-all ${activeTab === 'all'
                                ? 'bg-white dark:bg-surface-700 shadow-sm text-mavi-600 dark:text-mavi-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                        >
                            Tümü
                        </button>
                        <button
                            onClick={() => setActiveTab('joined')}
                            className={`px-4 py-2 text-sm font-poppins font-bold rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'joined'
                                ? 'bg-white dark:bg-surface-700 shadow-sm text-mavi-600 dark:text-mavi-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                        >
                            <Star className="w-3.5 h-3.5" />
                            Katıldıklarım
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative max-w-2xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Oda ismi veya konu ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-poppins"
                        />
                    </div>
                </div>
            </div>

            {/* State Handling */}
            {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white dark:bg-surface-900 rounded-2xl p-5 border border-surface-200 dark:border-surface-700 h-32 animate-pulse" />
                    ))}
                </div>
            ) : filteredRooms.length === 0 ? (
                <div className="py-20 text-center space-y-4 bg-white dark:bg-surface-900 rounded-2xl border border-dashed border-surface-300 dark:border-surface-700">
                    <div className="w-16 h-16 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center mx-auto text-gray-400">
                        <Globe className="w-8 h-8" />
                    </div>
                    <div>
                        <h3 className="font-poppins font-bold text-gray-900 dark:text-white">Henüz hiç oda yok</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">İlk odayı sen başlatmaya ne dersin?</p>
                    </div>
                    {!pubkey && (
                        <p className="text-xs text-mavi-500 font-poppins font-bold">Oda oluşturmak için giriş yapmalısın.</p>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredRooms.map((room) => (
                        <motion.div
                            key={room.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => handleRoomClick(room.identifier)}
                            className="group bg-white dark:bg-surface-900 rounded-2xl p-5 border border-surface-200 dark:border-surface-700 hover:border-mavi-500/50 transition-all cursor-pointer shadow-sm hover:shadow-md"
                        >
                            <div className="flex gap-4">
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-surface-100 dark:bg-surface-800 flex-shrink-0 flex items-center justify-center text-mavi-500">
                                    {room.picture ? (
                                        <img src={room.picture} alt={room.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    ) : (
                                        <Users className="w-8 h-8 opacity-20" />
                                    )}
                                </div>

                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                    <div>
                                        <h3 className="font-poppins font-bold text-lg text-gray-900 dark:text-white truncate group-hover:text-mavi-500 transition-colors">
                                            {room.name}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1 leading-relaxed">
                                            {room.about || 'Açıklama belirtilmemiş.'}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4 mt-3">
                                        <div className="flex items-center gap-1.5 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">
                                            <Users className="w-3 h-3 text-mavi-500" />
                                            GRUP
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">
                                            <MessageSquare className="w-3 h-3 text-orange-400" />
                                            AKTİF
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Global Rooms Teaser */}
            <div className="bg-gradient-to-br from-mavi-500/5 to-transparent dark:from-mavi-500/10 dark:to-transparent rounded-2xl p-8 border border-mavi-500/10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6 text-center md:text-left">
                    <div className="w-16 h-16 bg-white dark:bg-surface-900 rounded-2xl shadow-xl flex items-center justify-center text-mavi-500 flex-shrink-0">
                        <Zap className="w-8 h-8" />
                    </div>
                    <div>
                        <h2 className="font-poppins font-bold text-xl text-gray-800 dark:text-white">Nostr Ağını Keşfet</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Uzak relay'lerdeki binlerce genel odaya yakında buradan erişebileceksin.
                        </p>
                    </div>
                </div>
                <div className="px-5 py-2 bg-mavi-500/10 text-mavi-500 text-[10px] font-poppins font-bold rounded-full border border-mavi-500/20 uppercase tracking-widest">
                    Çok Yakında
                </div>
            </div>

            <CreateRoomModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </div>
    )
}
